import { ZETRIX_RESPONSE_MESSAGE } from "./requestEnum";
import {
  mainChainId,
  chainIdMessage,
  testChainIdMessage,
} from "./dictionary";
import { mapMutations } from "vuex";
import ZetrixWalletConnect from "zetrix-connect-wallet-sdk";
import { isMobile, isChrome } from "./util";
import { authTokenService, tokenStorage } from "../services/authToken.service";

const IS_TESTNET = process.env.VUE_APP_WALLET_TESTNET === "true";
const _walletConnectOpts = (qrcode) => ({
  testnet: IS_TESTNET,
  appType: "zetrixwv3",
  qrcode,
});

export default {
  data() {
    return {
      ztx: window.zetrix,
      isMobile: false,
      isChrome: false,
      zetrixWalletConnect: new ZetrixWalletConnect(_walletConnectOpts(false)),
      zetrixAccount: sessionStorage.getItem("zetrixAccount"),
      balance: this.$store.state.balance,
      isLogin: sessionStorage.getItem("isLogin"),
      errorNetwork: "",
      mobileStep: "idle", // 'idle' | 'connecting' | 'ready' | 'waiting' | 'sign-ready' | 'signing'
      pendingSign: null,  // { address, nonce } after auth succeeds
    };
  },

  created() {
    // Timer IDs are non-reactive — initialized here to avoid Vue proxy warnings
    // for underscore-prefixed properties declared inside data().
    this._authTimeout = null;
    this._signTimeout = null;
  },

  watch: {
    ztx: {
      deep: true,
      handler: function () {
        this.ztx.on("chainChanged", (res) => {
          if (res.code === 0) {
            this.setChainId(res.data.chainId);
            if (!this.isMainNetwork()) {
              this.disconnectWalletZtx();
            }
          }
        });
        this.ztx.on("switchAccount", (res) => {
          if (res.code === 0) {
            const newAddress = res.data.address;
            if (newAddress !== sessionStorage.getItem("zetrixAccount")) {
              // Token belongs to the old address — clear everything and force re-login
              tokenStorage.clearTokens();
              sessionStorage.clear();
              window.location.href = "/";
            }
          }
        });
      },
    },
  },

  mounted() {
    this.isMobile = isMobile();
    this.isChrome = isChrome();
    this.checkBalanceZTX();
  },

  updated() {
    this.checkWindowZetrix();
  },

  methods: {
    ...mapMutations([
      "setHeaderMessage",
      "setChainId",
      "setBalance",
    ]),

    checkWindowZetrix() {
      if (typeof window.zetrix !== "undefined") {
        this.ztx = window.zetrix;
      }
    },

    checkBalanceZTX() {
      if (this.zetrixAccount !== null) {
        this.getAccountBalance(this.zetrixAccount);
      }
    },

    checkZetrixInstall() {
      if (this.isMobile) {
        this.prepareMobileConnect();
      } else if (this.isChrome) {
        if (typeof window.zetrix === "undefined") {
          this.$store.dispatch(
            "triggerSnackbar",
            "Zetrix Wallet extension not detected. Opening the Chrome Web Store to install it."
          );
          window.open(
            "https://chromewebstore.google.com/detail/zetrix-wallet/lpjcijieclbbmdckfhomhfdnlfjelfmi"
          );
        } else {
          this.ztx = window.zetrix;
          this.ztx.getChainId((res) => {
            if (res.code === 0) {
              this.setChainId(res.data.chainId);
              this.sendClientAuthorize();
            }
          });
        }
      } else {
        // Desktop non-Chrome (Firefox, Safari, Edge) — QR code scan via phone.
        // No iOS gesture-context issue here, so a single async chain is correct.
        // APP-L03: close existing connection before creating a new one on retry.
        try { this.zetrixWalletConnect?.closeConnect(); } catch {}
        this.zetrixWalletConnect = new ZetrixWalletConnect(_walletConnectOpts(true));
        this.connectWalletQrCode();
      }
    },

    sendClientAuthorize() {
      if (!this.isMainNetwork()) {
        this.$store.dispatch("triggerSnackbar", this.errorNetwork);
        return false;
      }
      this.ztx.authorize({ method: "changeAccounts" }, async (resp) => {
        if (resp.code === 0) {
          const address = resp.data.address;
          try {
            // 1. Get single-use nonce from ms-public-proxy
            const nonceResp = await authTokenService.getNonce(address);
            const nonce = nonceResp.data.nonce;
            const nonceHex = Array.from(new TextEncoder().encode(nonce))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('');

            // 2. Use sendRandom (wallet-integrated one-time sign) instead of signMessage
            //    This prevents the changeAccounts persistent listener from re-triggering signBlob
            //    sendRandom expects the nonce pre-encoded as hex
            this.ztx.authorize(
              { method: "sendRandom", param: { random: nonceHex } },
              async (resAuth) => {
                if (resAuth.code === 0) {
                  try {
                    // 3. Exchange signed nonce for access + refresh tokens
                    const { publicKey, signData } = resAuth.data;
                    // Pass original nonce — backend hex-encodes it when verifying the sendRandom signature
                    const tokenResp = await authTokenService.createToken(address, publicKey, nonce, signData);
                    tokenStorage.setTokens(tokenResp.data.token, tokenResp.data.refreshToken);

                    sessionStorage.setItem("zetrixAccount", address);
                    this.zetrixAccount = address;
                    this.getAccountBalance(address);
                    this.isLogin = "true";
                    sessionStorage.setItem("isLogin", this.isLogin);
                    window.location.href = "/#/dashboard";
                  } catch (err) {
                    console.error("Token issuance failed:", err);
                    alert("Authentication failed. Please try again.");
                  }
                } else {
                  switch (resAuth.code) {
                    case 1:
                      alert(ZETRIX_RESPONSE_MESSAGE.CANCEL);
                      break;
                    case -1:
                      alert(ZETRIX_RESPONSE_MESSAGE.FAIL);
                      break;
                    default:
                      alert("Sign request failed. Please try again.");
                  }
                }
              }
            );
          } catch (err) {
            console.error("Failed to get nonce:", err);
            alert("Authentication failed. Please try again.");
          }
        } else {
          switch (resp.code) {
            case 1:
              alert(ZETRIX_RESPONSE_MESSAGE.CANCEL);
              break;
            case -1:
              alert(ZETRIX_RESPONSE_MESSAGE.FAIL);
              break;
            case 100001:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100001);
              break;
            case 100002:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100002);
              break;
            case 100003:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100003);
              break;
            case 100004:
              alert(ZETRIX_RESPONSE_MESSAGE.CODE100004);
              break;
          }
        }
      });
    },

    // APP-M04: check network before connecting; APP-M03: show snackbar on failure.
    async connectWalletQrCode() {
      if (!this.isMainNetwork()) {
        this.$store.dispatch("triggerSnackbar", this.errorNetwork);
        return;
      }
      try {
        // connect() with qrcode:true shows the SDK's QR modal — user scans with phone.
        await this.zetrixWalletConnect.connect();
        const res = await this.zetrixWalletConnect.auth();
        if (!res || res.code !== 0) return;
        const address = res.data.address;

        const nonceResp = await authTokenService.getNonce(address);
        const nonce = nonceResp.data.nonce;

        const signResult = await this.zetrixWalletConnect.signMessage({ message: nonce });
        if (!signResult || signResult.code !== 0) return;

        const { publicKey, signData } = signResult.data;
        const tokenResp = await authTokenService.createToken(address, publicKey, nonce, signData);
        tokenStorage.setTokens(tokenResp.data.token, tokenResp.data.refreshToken);

        sessionStorage.setItem("zetrixAccount", address);
        this.zetrixAccount = address;
        this.getAccountBalance(address);
        this.isLogin = "true";
        sessionStorage.setItem("isLogin", this.isLogin);
        window.location.href = "/#/dashboard";
      } catch (err) {
        console.error("QR code wallet connection failed:", err);
        this.$store.dispatch("triggerSnackbar", "QR wallet connection failed. Please try again.");
      }
    },

    isMainNetwork() {
      const chainId = this.$store.state.chainId;
      const host = `${window.location.protocol}//${window.location.host}`;
      const url = process.env.VUE_APP_API_BASE_URL;

      if (
        (host.includes("localhost") ||
          host.includes("test") ||
          host.includes("dev") ||
          host.includes("sandbox") ||
          host.includes("netlify") ||
          url.includes("localhost") ||
          url.includes("test") ||
          url.includes("dev") ||
          url.includes("sandbox") ||
          url.includes("netlify")) &&
        chainId === mainChainId
      ) {
        this.setHeaderMessage({
          show: true,
          message: testChainIdMessage,
        });
        this.errorNetwork = testChainIdMessage;
        return false;
      } else if (
        !host.includes("localhost") &&
        !host.includes("test") &&
        !host.includes("dev") &&
        !host.includes("sandbox") &&
        !host.includes("netlify") &&
        !url.includes("localhost") &&
        !url.includes("test") &&
        !url.includes("dev") &&
        !url.includes("sandbox") &&
        !url.includes("netlify") &&
        chainId !== mainChainId
      ) {
        this.setHeaderMessage({
          show: true,
          message: chainIdMessage,
        });
        this.errorNetwork = chainIdMessage;
        return false;
      }
      this.setHeaderMessage({
        show: false,
        message: "",
      });
      return true;
    },

    // --- Mobile three-tap state machine ---
    // State flow: idle → connecting → ready → waiting → sign-ready → signing → idle

    // APP-M01: wrap connect() in try/catch — mobileStep resets to idle on failure.
    async prepareMobileConnect() {
      if (this.mobileStep !== "idle") return;
      this.mobileStep = "connecting";
      // closeConnect() closes the actual WebSocket; disconnect() only clears storage.
      try { this.zetrixWalletConnect?.closeConnect(); } catch {}
      this.zetrixWalletConnect = new ZetrixWalletConnect(_walletConnectOpts(false));
      try {
        await this.zetrixWalletConnect.connect();
        this.mobileStep = "ready";
      } catch (err) {
        console.error("Mobile WS connect failed:", err);
        this.mobileStep = "idle";
        this.$store.dispatch("triggerSnackbar", "Could not connect to wallet service. Please try again.");
      }
    },

    openWalletApp() {
      if (this.mobileStep !== "ready") return;
      this.mobileStep = "waiting";
      this._authTimeout = setTimeout(() => {
        if (this.mobileStep === "waiting") this.cancelMobileConnect();
      }, 90000);

      // Safety net: if the pre-connect socket dropped, reconnect before auth().
      // If already OPEN this check is synchronous (no await) so gesture context is preserved.
      // walletSocket._websocket is an internal of zetrix-connect-wallet-sdk v1.2.15.
      // Track SDK upgrades — request a public isConnected() getter from the SDK team.
      const _ws = this.zetrixWalletConnect?.walletSocket?._websocket;
      if (!_ws || _ws.readyState !== WebSocket.OPEN) {
        this.zetrixWalletConnect.connect().catch(() => {});
      }

      // auth() fires the zetrix:// deep link synchronously — gesture context preserved.
      const authPromise = this.zetrixWalletConnect.auth();
      authPromise
        .then(async (res) => {
          clearTimeout(this._authTimeout);
          this._authTimeout = null;
          if (!res || res.code !== 0) {
            this.cancelMobileConnect();
            this.$store.dispatch("triggerSnackbar", "Authorization rejected. Please try again.");
            return;
          }
          const address = res.data.address;
          const nonceResp = await authTokenService.getNonce(address);
          const nonce = nonceResp.data.nonce;
          // iOS drops the WebSocket during the app switch to the wallet.
          // Reconnect with a fresh instance to re-register the session.
          await this.afterOpenToBind();
          this.pendingSign = { address, nonce };
          this.mobileStep = "sign-ready";
        })
        // APP-M02: show user-facing error on auth failure.
        .catch(() => {
          this.cancelMobileConnect();
          this.$store.dispatch("triggerSnackbar", "Wallet connection lost. Please try again.");
        });
    },

    async afterOpenToBind() {
      // Close the old socket first — without closeConnect() the old session stays alive
      // server-side and sign responses route to it instead of the fresh connection.
      try { this.zetrixWalletConnect?.closeConnect(); } catch {}
      const fresh = new ZetrixWalletConnect(_walletConnectOpts(false));
      await fresh.connect();
      this.zetrixWalletConnect = fresh;
    },

    confirmSign() {
      if (this.mobileStep !== "sign-ready" || !this.pendingSign) return;
      this.mobileStep = "signing";
      this._signTimeout = setTimeout(() => {
        if (this.mobileStep === "signing") this.cancelMobileConnect();
      }, 90000);

      const { address, nonce } = this.pendingSign;
      // signMessage() fires the zetrix:// deep link synchronously — gesture context preserved.
      const signPromise = this.zetrixWalletConnect.signMessage({ message: nonce });
      signPromise
        .then(async (signResult) => {
          clearTimeout(this._signTimeout);
          this._signTimeout = null;
          if (!signResult || signResult.code !== 0) {
            this.cancelMobileConnect();
            this.$store.dispatch("triggerSnackbar", "Sign request rejected. Please try again.");
            return;
          }
          const { publicKey, signData } = signResult.data;
          const tokenResp = await authTokenService.createToken(
            address,
            publicKey,
            nonce,
            signData
          );
          tokenStorage.setTokens(tokenResp.data.token, tokenResp.data.refreshToken);
          sessionStorage.setItem("zetrixAccount", address);
          this.zetrixAccount = address;
          this.getAccountBalance(address);
          this.isLogin = "true";
          sessionStorage.setItem("isLogin", this.isLogin);
          this.pendingSign = null;
          this.mobileStep = "idle";
          window.location.href = "/#/dashboard";
        })
        .catch(() => {
          this.cancelMobileConnect();
          this.$store.dispatch("triggerSnackbar", "Sign failed. Please try again.");
        });
    },

    cancelMobileConnect() {
      clearTimeout(this._authTimeout);
      clearTimeout(this._signTimeout);
      this._authTimeout = null;
      this._signTimeout = null;
      try { this.zetrixWalletConnect?.closeConnect(); } catch {}
      try { this.zetrixWalletConnect?.disconnect(); } catch {}
      this.zetrixWalletConnect = new ZetrixWalletConnect(_walletConnectOpts(false));
      this.pendingSign = null;
      this.mobileStep = "idle";
    },

    getAccountBalance(zetrixAccount) {
      this.$store
        .dispatch("wallet/getAccountBalance", { zetrixAccount: zetrixAccount })
        .then((response) => {
          if (response != null) {
            this.setBalance(response.balance);
            this.balance = response.balance;
          } else {
            this.setBalance(0);
            this.balance = 0;
          }
        });
    },

    signBlob(blob) {
      return new Promise((resolve) => {
        setTimeout(() => {
          if (typeof this.ztx !== "undefined") {
            this.ztx.signMessage({ message: blob }, function (result) {
              if (result.code === 0) {
                return resolve(result);
              }
              return resolve(false);
            });
          } else {
            return resolve(false);
          }
        }, 0);
      });
    },

    // Intentionally retains signBlob internals — many pages depend on this method and are not
    // yet migrated to signMessage. Do not rename or change internals until all call sites are
    // migrated. Track in BT-2262 (did-management-platform) / BT-2246 (multisig).
    signBlobMobileZtx(blob) {
      return this.zetrixWalletConnect
        .signBlob({ message: blob })
        .then((res) => (res.code === 0 ? res : false))
        .catch(() => false);
    },

    disconnectWalletZtx() {
      if (this.isMobile || !this.isChrome) {
        try { this.zetrixWalletConnect?.closeConnect(); } catch {}
        try { this.zetrixWalletConnect?.disconnect(); } catch {}
      }
      tokenStorage.clearTokens();
      sessionStorage.clear();
      window.location.href = "/";
    },
  },
};
